/**
 * HistorieVozuModal — the history of one fleet car (P4-i, per-row modal, read-only): ONE timeline, newest first, joined by the API
 * from what is stored — rentals out / back, faults found and repaired, checks done, manual odometer readings, enrolment / take-out.
 * Nothing is edited here. A kind filter (Chip + Menu) narrows it; "Načíst další" pages it. Renders only from a read that finished
 * after the modal opened (never a cached snapshot), like the other fleet modals.
 */

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { Icon } from '@/components/Icon';
import { Menu } from '@/components/Menu';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { useState } from 'react';
import { useHistorieVozuQuery, type HistorieSkupina } from './api';
import { casUdalosti, popisUdalosti, SKUPINY } from './historie-vozu-helpers';

interface HistorieVozuModalProps {
  /** The fleet entry id. */
  id: number;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HistorieVozuModal({ id, title, open, onOpenChange }: HistorieVozuModalProps) {
  const [skupina, setSkupina] = useState<HistorieSkupina | undefined>(undefined);
  const query = useHistorieVozuQuery(id, skupina);
  // `data` (not `isSuccess`): a failed "next page" turns the query into an error but keeps what was loaded
  const fresh = query.data != null && query.isFetchedAfterMount;
  // Pages are offsets into a live, newest-first list: an event written meanwhile shifts the next page by one, so a line can come twice.
  const videno = new Set<string>();
  const udalosti = (query.data?.pages.flatMap((p) => p.udalosti) ?? []).filter((u) => !videno.has(u.id) && !!videno.add(u.id));
  const celkem = query.data?.pages[0]?.celkem ?? 0;
  const popisek = SKUPINY.find((s) => s.value === skupina)?.label ?? 'Vše';

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={760}>
      <DialogCrown
        eyebrow="Vozidlo půjčovny"
        title={
          <>
            Historie <em>{title}</em>
          </>
        }
        sub="Předání a vrácení, závady, kontroly, odečty nájezdu a zařazení do půjčovny — od nejnovějšího."
      />
      <Modal.Body className="px-8 py-5">
        <div className="mb-4 flex items-center gap-3">
          <Menu>
            <Menu.Trigger asChild>
              <Chip icon={<Icon name="filter" size={12} />} active={skupina !== undefined} aria-label="Druh událostí" data-testid="historie-vozu-skupina">
                {popisek}
              </Chip>
            </Menu.Trigger>
            <Menu.Content>
              <Menu.Label>Druh událostí</Menu.Label>
              {SKUPINY.map((s) => (
                <Menu.CheckItem key={s.label} checked={skupina === s.value} onCheckedChange={(checked) => checked && setSkupina(s.value)}>
                  {s.label}
                </Menu.CheckItem>
              ))}
            </Menu.Content>
          </Menu>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3" data-testid="historie-vozu-pocet">
            {fresh ? `${celkem} událostí` : '—'}
          </span>
        </div>

        {query.isError && !query.isFetchNextPageError ? (
          <p data-testid="historie-vozu-error" className="text-[13px] text-ink-2">
            Historii se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
          </p>
        ) : !fresh ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : udalosti.length === 0 ? (
          <p data-testid="historie-vozu-prazdna" className="text-[13px] text-ink-2">
            {skupina ? 'Tohoto druhu zatím nic není.' : 'U vozidla zatím nic není zaznamenáno.'}
          </p>
        ) : (
          <ol data-testid="historie-vozu-seznam" className="border border-line">
            {udalosti.map((u) => {
              const r = popisUdalosti(u);
              return (
                <li key={u.id} data-druh={u.druh} className="flex flex-wrap items-start gap-x-4 gap-y-1 border-b border-line px-3 py-2.5 last:border-b-0">
                  <span className="w-[8.5rem] shrink-0 font-mono text-[11.5px] text-ink-2">{casUdalosti(u)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <Badge tone={r.ton} size="sm">
                        {r.nadpis}
                      </Badge>
                      {u.kdo && <span className="text-[12px] text-ink-3">{u.kdo}</span>}
                    </span>
                    {r.radky.map((radek, i) => (
                      <span key={i} className="mt-0.5 block text-[13px] text-ink [overflow-wrap:anywhere]">
                        {radek}
                      </span>
                    ))}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
        {query.isFetchNextPageError && (
          <p data-testid="historie-vozu-dalsi-error" className="mt-2 text-[13px] text-ink-2">
            Další události se nepodařilo načíst.
          </p>
        )}
        {fresh && query.hasNextPage && (
          <div className="mt-3">
            <Button variant="secondary" size="sm" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              Načíst další ({celkem - udalosti.length})
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Zavřít
          </Button>
        }
      />
    </Modal>
  );
}
